import JsonApiResource from './JsonApiResource'
import AnearZone from './AnearZone'
import AnearUser from './AnearUser'
import userMessaging from '../Utils/UserMessaging'

const BroadcastMessageType           = 'BROADCAST'
const SpectatorsDisplayMessageType   = 'SPECTATORS_DISPLAY'
const EventTransitionMessageType     = 'EVENT_TRANSITION'
const EventQuiescedMessageType       = 'EVENT_QUIESCED'
const PlayAgainMessageType           = 'PLAY_AGAIN'

// State constants (matching backend route/runtime semantics)
const END_STATES          = ['closed', 'archive', 'canceled']
const POST_LIVE_STATES    = ['review', 'reward', 'concluded']
const PARTICIPANT_STATES  = ['announce', 'live', 'paused', 'review', 'reward']
const HOST_CONTROL_STATES = ['created', 'announce', 'live', 'paused', 'review', 'reward', 'concluded']
const OPENING_STATES      = ['announce', 'created']
const NEEDS_REFRESH_STATES= ['announce', 'live', 'review', 'reward', 'concluded', 'closed']
const LIVE_STATE          = 'live'
const CONCLUDED_STATE     = 'concluded'
const CTX_PLAY_AGAIN_INV  = 'play_again_invitable'
const CTX_PARTICIPANT     = 'participant'
const CTX_PARTICIBLE      = 'particible'
const CTX_CREATOR         = 'creator'
const DEFAULT_HOST_ROLE = Object.freeze({
  eventRoleId: null,
  name: 'Host',
  slug: 'host',
  description: 'Default host access for managing and running the event.'
})

const normalizeRoleSlug = (value) => String(value || '').trim().toLowerCase()
const normalizeRolePayload = (role) => {
  const name = role?.name || DEFAULT_HOST_ROLE.name
  const slug = role?.slug || role?.['slug'] || name.toLowerCase().replace(/\s+/g, '-')

  return {
    eventRoleId: role?.eventRoleId ?? role?.['event-role-id'] ?? null,
    name,
    slug,
    description: role?.description || null
  }
}

const normalizeHostRoleData = (roleData) => {
  const rawRoles = Array.isArray(roleData?.roles)
    ? roleData.roles
    : Array.isArray(roleData?.['roles'])
      ? roleData['roles']
      : (roleData && (roleData.eventRoleId || roleData['event-role-id'] || roleData.name || roleData.slug))
          ? [roleData]
          : []

  const assignedRoles = rawRoles
    .map(normalizeRolePayload)
    .filter((role, index, items) => (
      items.findIndex((candidate) => (
        (candidate.eventRoleId && candidate.eventRoleId === role.eventRoleId) ||
        normalizeRoleSlug(candidate.slug) === normalizeRoleSlug(role.slug)
      )) === index
    ))

  // Matches ANAPI EventRole.build_runtime_role_data: no app-defined roles => implicit full host (empty catalog).
  if (assignedRoles.length === 0) {
    return {
      roles: [],
      roleNames: [],
      primaryRole: DEFAULT_HOST_ROLE,
      implicitHost: true
    }
  }

  return {
    roles: assignedRoles,
    roleNames: assignedRoles.map((role) => normalizeRoleSlug(role.slug)),
    primaryRole: assignedRoles[0],
    implicitHost: false
  }
}

export const parseEventTransitionContent = (message) => {
  const content = message?.data?.content || message?.data || {}
  return {
    from: content.from || content.old_state || content['old-state'] || null,
    to: content.to || content.state || null,
    message: content.message || null,
    source: content.source || null
  }
}


export default class AnearEvent extends JsonApiResource {
  // represents the state of the event at time of fetch
  // from the Anear API.  Ably event transition messages can
  // change the live state kept in the EventPage state.
  //
  constructor(json_data, json_included) {
    super(json_data, json_included)
    this.zone = new AnearZone(this.findIncluded(this.relationships['zone']), this.included)
    this.user = new AnearUser(this.findIncluded(this.relationships['user']), this.included)
    this._channels =  {
      eventChannel: null,
      spectatorsChannel: null
    }
  }

  get name() { return this.attributes.name }
  get slug() { return this.attributes.slug }
  get short_url() { return `/e/${this.slug}` }
  get createdAt() { return this.attributes['created-at'] }
  get state() { return this.attributes.state } // anear event state "closed", "announce", etc
  get context() { return this.attributes.context } // Auth'd users relationship to the event e.g. "creator"
  get cssUrl() { return this.attributes['css-url'] }
  get eventHosts() {
    const eventHosts = this.attributes['event-hosts'] || []

    return eventHosts.map((eventHost) => ({
      ...eventHost,
      roleData: normalizeHostRoleData(eventHost?.roleData || eventHost?.['role-data'])
    }))
  }

  findEventHostByUserId(userId) {
    return this.eventHosts.find((eventHost) => eventHost.userId === userId || eventHost['user-id'] === userId) || null
  }

  getHostRolesForUser(userId) {
    if (!this.hosted) return []
    if (userId === this.user.id) return [DEFAULT_HOST_ROLE]

    const eventHost = this.findEventHostByUserId(userId)
    if (!eventHost) return []
    const { roles, implicitHost } = eventHost.roleData
    if (implicitHost) return []
    return roles
  }

  getHostRoleNamesForUser(userId) {
    if (!this.hosted) return []
    if (userId === this.user.id) return [normalizeRoleSlug(DEFAULT_HOST_ROLE.slug)]

    const eventHost = this.findEventHostByUserId(userId)
    if (!eventHost) return []
    return eventHost.roleData.roleNames
  }

  hasHostRole(userId, roleName) {
    const slug = normalizeRoleSlug(roleName)
    if (userId === this.user.id && slug === 'host') return true

    const eventHost = this.findEventHostByUserId(userId)
    if (!eventHost) return false

    const { roleNames, implicitHost } = eventHost.roleData
    if (slug === 'host' && implicitHost) return true
    return roleNames.includes(slug)
  }

  set state(newState) { this.state = newState }
  set context(newContext) { this.context = newContext }

    // ==== state-checkers ====
  isCreator() {
    const ctx = this.context
    if (!ctx) return false
    if (Array.isArray(ctx)) return ctx.includes(CTX_CREATOR)
    if (typeof ctx === 'string') return ctx.includes(CTX_CREATOR)
    return false
  }
  isParticipant() { return this.context.includes(CTX_PARTICIPANT) }
  isPlayAgainInvitable() { return this.context.includes(CTX_PLAY_AGAIN_INV) }
  isSpectator()   { return !this.isParticipant() }
  isOpening()     { return OPENING_STATES.includes(this.state) }
  isParticible() {
    const ctx = this.context
    if (!ctx) return false
    if (Array.isArray(ctx)) return ctx.includes(CTX_PARTICIBLE)
    if (typeof ctx === 'string') return ctx.includes(CTX_PARTICIBLE)
    return false
  }
  isLive()        { return this.state === LIVE_STATE }
  isOver()        { return END_STATES.includes(this.state) }
  isInProgress()  { return !this.isOver() }
  isPostLive()    { return POST_LIVE_STATES.includes(this.state) }
  isConcluded()   { return this.state === CONCLUDED_STATE }
  supportsParticipantChannels() { return PARTICIPANT_STATES.includes(this.state) }
  supportsHostControl() { return HOST_CONTROL_STATES.includes(this.state) }
  needsRefresh(newState) {
    return this.state !== newState && NEEDS_REFRESH_STATES.includes(newState)
  }

  // A hosted event possibly has a participant who only acts as the moderator or "host".
  // He/she typically does not participate in game play.  E.g.  Trivia contest organizer and master of ceremonies.
  // Or, a merchnat sponsoring an event at his place of business
  get hosted() { return this.attributes.hosted }
  get canPlayAgain() { return this.zone.canPlayAgain }

  requiresGeoLocationToParticipate = () => {
    // if the event has a participation radius, then a participant must
    // have a geoLocation set in the browser for validation by the Anear API
    return this.attributes['participation-radius']
  }

  spectatorsAllowed = () => {
    // Canonical spectators flag provided by ANAPI EventSerializer
    return this.attributes['spectators-allowed'] === true
  }

  isOpenHouse = () => {
    // Check open_house flag from app via zone relationship (app is included in event serializer)
    // AppSerializer exposes open_house as a convenience attribute, or check flags array
    if (this.zone && this.zone.app) {
      return this.zone.app.openHouse || false
    }
    return false
  }

  initEventChannels = () => {
    if (!this._channels.eventChannel) {
      this._channels.eventChannel = userMessaging.getChannel(this.attributes['event-channel-name'])
    }
  }

  initMessaging = () => {
    // Register app slug for logging attribution in UserMessaging
    try {
      const appAttrs = this.zone?.app?.attributes || {}
      const appSlug =
        appAttrs.slug ||
        appAttrs['slug'] ||
        // Fallback: short-name is better than nothing for attribution
        appAttrs['short-name'] ||
        null
      // Note: Ably channel names use compact_id(event.id), not UUID. Register both.
      userMessaging.registerEventAppSlug(this.id, appSlug)
      userMessaging.registerEventAppSlugForChannel(this.attributes['event-channel-name'], appSlug)
    } catch (_e) {
      // ignore
    }
    this.initEventChannels()
  }

  initSpectatorMessaging = () => {
    if (this.spectatorsAllowed() && !this._channels.spectatorsChannel) {
      this._channels.spectatorsChannel = userMessaging.getChannel(this.attributes['spectators-channel-name'])
    }
  }

  detachAll = async () => {
    const detachPromises = []

    if (this._channels.eventChannel) {
      // Match participant teardown: Ably can retain channel.subscribe listeners across detach/reattach.
      this.unSubscribeEventBroadcastMessages()
      this.unSubscribeEventTransitionMessages()
      this.unSubscribeEventQuiescedMessages()
      this.unSubscribeEventPlayAgainMessages()
      detachPromises.push(userMessaging.detachChannel(this._channels.eventChannel))
      this._channels.eventChannel = null
    }

    // Add the closeSpectatorsChannel() call unconditionally since it doesn't have a null check
    detachPromises.push(this.closeSpectatorsChannel())

    // Await all non-null detach operations
    await Promise.all(detachPromises)

    // Set channels to null after detaching
  }

  closeMessaging = async () => {
    await this.detachAll()
  }

  subscribeEventBroadcastMessages = callback => {
    userMessaging.subscribeEventMessages(this._channels.eventChannel, BroadcastMessageType, callback)
  }

  subscribeEventTransitionMessages = callback => {
    userMessaging.subscribeEventMessages(this._channels.eventChannel, EventTransitionMessageType, callback)
  }

  // EVENT_QUIESCED is a non-lifecycle control message published by the AEM on the
  // event channel when it is gracefully shutting down to free realtime resources
  // (AppM-driven, typically in response to long-running idleness). The persisted
  // AppM snapshot is intact and ANAPI event state is unchanged. The handler is
  // responsible for tearing down channels (resource conservation) and surfacing a
  // rejoin overlay; clicking the overlay reloads the page, which re-enters presence
  // on :actions and triggers ANAPI LOAD_EVENT to rehydrate the AEM from the snapshot.
  //
  // Do not replay Ably history. Rejoin is a reload; history of the prior
  // EVENT_QUIESCED would immediately re-show the overlay and detach the
  // channels that LOAD_EVENT is trying to bring back.
  subscribeEventQuiescedMessages = callback => {
    userMessaging.subscribeEventMessages(this._channels.eventChannel, EventQuiescedMessageType, callback, false)
  }

  subscribeEventPlayAgainMessages = callback => {
    userMessaging.subscribeEventMessages(this._channels.eventChannel, PlayAgainMessageType, callback)
  }

  subscribeEventPresenceEnterMessages = callback => {
    userMessaging.subscribePresenceEnterEvents(this._channels.eventChannel, callback)
  }

  subscribeSpectatorDisplayMessages = async (anearUser, displayCallback) => {
    this.initSpectatorMessaging()
    userMessaging.subscribeEventMessages(this._channels.spectatorsChannel, SpectatorsDisplayMessageType, displayCallback, false)
    await userMessaging.setChannelPresence(this._channels.spectatorsChannel, anearUser.id)
  }

  unSubscribeEventBroadcastMessages = () => {
    userMessaging.unSubscribeEventMessages(this._channels.eventChannel, BroadcastMessageType)
  }

  unSubscribeEventTransitionMessages = () => {
    userMessaging.unSubscribeEventMessages(this._channels.eventChannel, EventTransitionMessageType)
  }

  unSubscribeEventQuiescedMessages = () => {
    userMessaging.unSubscribeEventMessages(this._channels.eventChannel, EventQuiescedMessageType)
  }

  unSubscribeEventPlayAgainMessages = () => {
    userMessaging.unSubscribeEventMessages(this._channels.eventChannel, PlayAgainMessageType)
  }

  closeSpectatorsChannel = async () => {
    if (!this._channels.spectatorsChannel) return

    userMessaging.unSubscribeEventMessages(this._channels.spectatorsChannel, SpectatorsDisplayMessageType)
    await userMessaging.detachChannel(this._channels.spectatorsChannel)
    this._channels.spectatorsChannel = null
  }
}
